import React, { useState, useEffect } from 'react';
import {
  User, Mail, Phone, Clock, Building, MapPin, Save, AlertCircle,
  CheckCircle, Eye, EyeOff, Lock, Bell, Shield, Key, Globe, MessageSquare,
  ChevronDown, X, AlertTriangle, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get API key from localStorage
const getApiKey = () => localStorage.getItem('customer_api_key');

export const SettingsPanel = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeSection, setActiveSection] = useState('profile');
  const [pendingChanges, setPendingChanges] = useState([]);

  // Form states
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
    timezone: 'America/New_York'
  });

  const [businessForm, setBusinessForm] = useState({
    greeting_message: '',
    business_hours: {}
  });

  const [notificationForm, setNotificationForm] = useState({
    email_summaries: true,
    sms_alerts: false,
    usage_warnings: true
  });

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Timezones
  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST)' },
    { value: 'America/Anchorage', label: 'Alaska (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' }
  ];

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Load settings and pending changes in parallel
      const [settingsRes, pendingRes] = await Promise.all([
        fetch(`${API_URL}/api/customers/settings`, {
          headers: { 'X-API-Key': getApiKey() }
        }),
        fetch(`${API_URL}/api/pending-changes/my-changes`, {
          headers: { 'X-API-Key': getApiKey() }
        })
      ]);

      if (!settingsRes.ok) throw new Error('Failed to load settings');

      const data = await settingsRes.json();
      setSettings(data);

      // Load pending changes
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingChanges(pendingData.pending || []);
      }

      // Populate forms
      setProfileForm({
        name: data.profile.name || '',
        email: data.profile.email || '',
        phone: data.profile.phone || '',
        timezone: data.profile.timezone || 'America/New_York'
      });

      setBusinessForm({
        greeting_message: data.business.greeting_message || '',
        business_hours: data.business.business_hours || {}
      });

      setNotificationForm(data.notifications || {
        email_summaries: true,
        sms_alerts: false,
        usage_warnings: true
      });

    } catch (err) {
      setError('Failed to load settings. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${API_URL}/api/customers/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': getApiKey()
        },
        body: JSON.stringify(profileForm)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to save profile');
      }

      setSuccess('Profile updated successfully!');
      
      // Update localStorage with new name
      if (profileForm.name) {
        localStorage.setItem('customer_client_name', profileForm.name);
      }
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveBusinessSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${API_URL}/api/customers/business-settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': getApiKey()
        },
        body: JSON.stringify(businessForm)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to save business settings');
      }

      // Update pending changes list
      if (data.pending_changes) {
        setPendingChanges(prev => [...data.pending_changes.map(c => ({
          ...c,
          status: 'pending',
          submitted_at: new Date().toISOString()
        })), ...prev]);
      }

      setSuccess(data.message || 'Changes submitted for approval!');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${API_URL}/api/customers/notifications`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': getApiKey()
        },
        body: JSON.stringify(notificationForm)
      });

      if (!res.ok) throw new Error('Failed to save notification preferences');

      setSuccess('Notification preferences updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetPassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (passwordForm.new.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    if (!/\d/.test(passwordForm.new)) {
      setPasswordError('Password must contain at least one number');
      return;
    }

    setSaving(true);

    try {
      // If user already has password, use change-password endpoint
      const endpoint = settings?.security?.has_password
        ? '/api/customers/change-password'
        : '/api/customers/set-password';

      const body = settings?.security?.has_password
        ? { old_password: passwordForm.current, new_password: passwordForm.new }
        : { password: passwordForm.new };

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': getApiKey()
        },
        body: JSON.stringify(body)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to update password');
      }

      setPasswordSuccess('Password updated successfully!');
      setPasswordForm({ current: '', new: '', confirm: '' });
      
      // Update settings to reflect password is now set
      setSettings(prev => ({
        ...prev,
        security: { ...prev.security, has_password: true }
      }));

      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateBusinessHours = (day, field, value) => {
    setBusinessForm(prev => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: {
          ...prev.business_hours[day],
          [field]: value
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const sections = [
    { id: 'profile', icon: User, label: 'Profile' },
    { id: 'business', icon: Building, label: 'Business' },
    { id: 'notifications', icon: Bell, label: 'Notifications' },
    { id: 'security', icon: Shield, label: 'Security' }
  ];

  return (
    <div className="space-y-4">
      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <StatCard title="ACCOUNT" subtitle="STATUS" value="Active" color="green" />
        <StatCard title="LOCATIONS" subtitle="CONFIGURED" value={settings?.locations_count || 1} color="blue" />
        <StatCard title="PLAN" subtitle="CURRENT" value={settings?.profile?.plan_id?.toUpperCase() || 'STARTER'} color="purple" />
        <StatCard title="SECURITY" subtitle="PASSWORD" value={settings?.security?.has_password ? 'Set' : 'Not Set'} color={settings?.security?.has_password ? 'green' : 'amber'} />
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <span className="text-green-700">{success}</span>
        </div>
      )}

      {/* Section Tabs */}
      <div className="bg-white rounded-lg border border-[#D6E2F3] overflow-hidden">
        <div className="flex border-b border-[#D6E2F3]">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeSection === section.id
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <section.icon className="w-4 h-4" />
              {section.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Profile Section */}
          {activeSection === 'profile' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Profile Information</h3>
                <p className="text-sm text-gray-500">Update your business details and contact information</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Building className="w-4 h-4 inline mr-1" />
                    Business Name
                  </label>
                  <input
                    type="text"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your Business Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="contact@business.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Globe className="w-4 h-4 inline mr-1" />
                    Timezone
                  </label>
                  <select
                    value={profileForm.timezone}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, timezone: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {timezones.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={saveProfile}
                  disabled={saving}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <span className="text-sm text-gray-500">
                  Client ID: <code className="bg-gray-100 px-2 py-1 rounded">{settings?.profile?.client_id}</code>
                </span>
              </div>
            </div>
          )}

          {/* Business Section */}
          {activeSection === 'business' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Business Settings</h3>
                <p className="text-sm text-gray-500">Configure how your AI assistant handles calls</p>
              </div>

              {/* Pending Changes Banner */}
              {pendingChanges.filter(c => c.change_type === 'greeting_message' || c.change_type === 'business_hours').length > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">Pending Approval</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        You have changes waiting for approval. Our team will review and apply them within 24 hours.
                      </p>
                      <ul className="mt-2 space-y-1">
                        {pendingChanges
                          .filter(c => c.change_type === 'greeting_message' || c.change_type === 'business_hours')
                          .map((change, idx) => (
                            <li key={idx} className="text-sm text-amber-600 flex items-center gap-2">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              {change.change_type === 'greeting_message' ? 'Greeting Message' : 'Business Hours'} - Submitted {new Date(change.submitted_at).toLocaleDateString()}
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  AI Greeting Message
                </label>
                <textarea
                  value={businessForm.greeting_message}
                  onChange={(e) => setBusinessForm(prev => ({ ...prev, greeting_message: e.target.value }))}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Thank you for calling [Business Name]. How may I help you today?"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {businessForm.greeting_message.length}/500 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Business Hours
                </label>
                <div className="space-y-3">
                  {days.map(day => {
                    const hours = businessForm.business_hours[day] || { open: '09:00', close: '17:00', closed: false };
                    return (
                      <div key={day} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                        <span className="w-24 font-medium text-gray-700 capitalize">{day}</span>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={!hours.closed}
                            onChange={(e) => updateBusinessHours(day, 'closed', !e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-600">Open</span>
                        </label>
                        {!hours.closed && (
                          <>
                            <input
                              type="time"
                              value={hours.open}
                              onChange={(e) => updateBusinessHours(day, 'open', e.target.value)}
                              className="px-3 py-1 border border-gray-300 rounded text-sm"
                            />
                            <span className="text-gray-500">to</span>
                            <input
                              type="time"
                              value={hours.close}
                              onChange={(e) => updateBusinessHours(day, 'close', e.target.value)}
                              className="px-3 py-1 border border-gray-300 rounded text-sm"
                            />
                          </>
                        )}
                        {hours.closed && (
                          <span className="text-sm text-gray-500 italic">Closed</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={saveBusinessSettings}
                  disabled={saving}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Submitting...' : 'Submit for Approval'}
                </button>
                <span className="text-sm text-gray-500">
                  Changes require admin approval before going live
                </span>
              </div>
            </div>
          )}

          {/* Notifications Section */}
          {activeSection === 'notifications' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Notification Preferences</h3>
                <p className="text-sm text-gray-500">Choose how you want to be notified about activity</p>
              </div>

              <div className="space-y-4">
                <NotificationToggle
                  label="Email Summaries"
                  description="Receive daily summaries of calls, leads, and bookings"
                  enabled={notificationForm.email_summaries}
                  onChange={(val) => setNotificationForm(prev => ({ ...prev, email_summaries: val }))}
                />
                <NotificationToggle
                  label="SMS Alerts"
                  description="Get text messages for urgent matters only"
                  enabled={notificationForm.sms_alerts}
                  onChange={(val) => setNotificationForm(prev => ({ ...prev, sms_alerts: val }))}
                />
                <NotificationToggle
                  label="Usage Warnings"
                  description="Alert when approaching minute limits"
                  enabled={notificationForm.usage_warnings}
                  onChange={(val) => setNotificationForm(prev => ({ ...prev, usage_warnings: val }))}
                />
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={saveNotifications}
                  disabled={saving}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            </div>
          )}

          {/* Security Section */}
          {activeSection === 'security' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Security Settings</h3>
                <p className="text-sm text-gray-500">Manage your password and API key</p>
              </div>

              {/* Password Section */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  {settings?.security?.has_password ? 'Change Password' : 'Set Password'}
                </h4>

                {passwordError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {passwordError}
                  </div>
                )}

                {passwordSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                    {passwordSuccess}
                  </div>
                )}

                <div className="space-y-4">
                  {settings?.security?.has_password && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                      <div className="relative">
                        <input
                          type={showPasswords ? 'text' : 'password'}
                          value={passwordForm.current}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords ? 'text' : 'password'}
                        value={passwordForm.new}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, new: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Min 8 characters with a number"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(!showPasswords)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      >
                        {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordForm.confirm}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <button
                    onClick={handleSetPassword}
                    disabled={saving || !passwordForm.new || !passwordForm.confirm}
                    className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Updating...' : settings?.security?.has_password ? 'Update Password' : 'Set Password'}
                  </button>
                </div>
              </div>

              {/* API Key Section */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  API Key
                </h4>
                <p className="text-sm text-gray-500 mb-3">Your API key for programmatic access</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-mono">
                    {settings?.security?.api_key_preview || '••••••••••••...'}
                  </code>
                  <button className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg">
                    View Full Key
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Keep your API key secure. Do not share it publicly.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ title, subtitle, value, color }) => {
  const colors = {
    green: 'bg-green-50 text-green-700',
    blue: 'bg-blue-50 text-blue-700',
    purple: 'bg-purple-50 text-purple-700',
    amber: 'bg-amber-50 text-amber-700'
  };

  return (
    <div className="bg-white rounded-lg p-4 border border-[#D6E2F3]">
      <div className="text-xs text-gray-500 uppercase tracking-wider">{title}</div>
      <div className="text-xs text-gray-400">{subtitle}</div>
      <div className={`mt-2 inline-block px-3 py-1 rounded-full text-sm font-semibold ${colors[color]}`}>
        {value}
      </div>
    </div>
  );
};

const NotificationToggle = ({ label, description, enabled, onChange }) => (
  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
    <div>
      <div className="font-medium text-gray-900">{label}</div>
      <div className="text-sm text-gray-500">{description}</div>
    </div>
    <button
      onClick={() => onChange(!enabled)}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        enabled ? 'bg-blue-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
          enabled ? 'left-7' : 'left-1'
        }`}
      />
    </button>
  </div>
);

export default SettingsPanel;
